import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Gavel } from "lucide-react";
import Countdown from "@/components/Countdown";
import { money, categoryLabel } from "@/lib/api";

export default function AuctionCard({ auction, index = 0 }) {
  const img = auction.hero_image || auction.photos?.[0] || "https://images.unsplash.com/photo-1649313444539-a8900c5cdc54?w=800&auto=format&fit=crop&q=80";
  const endMs = auction.end_time ? new Date(auction.end_time).getTime() - Date.now() : 0;
  const isEndingSoon = endMs > 0 && endMs < 60 * 60 * 1000;
  const isEnded = auction.status !== "active";
  return (
    <Link
      to={`/auctions/${auction.id}`}
      data-testid={`auction-card-${auction.id}`}
      className={`group block bg-zinc-900 border ${auction.is_featured ? "border-orange-500/60" : "border-zinc-800"} hover:border-zinc-600 hover:-translate-y-1 transition-[transform,border-color] duration-200 animate-fade-up`}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-950">
        <img
          src={img}
          alt={auction.title}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-[opacity,transform] duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent pointer-events-none" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {auction.is_featured && (
            <span className="inline-flex items-center gap-1.5 bg-orange-500 text-white px-2 py-1 text-[10px] font-semibold tracking-[0.2em] uppercase border border-orange-400">
              <span className="w-1 h-1 bg-white rounded-full"></span>
              Featured
            </span>
          )}
          {!isEnded && (
            <span className="inline-flex items-center gap-2 bg-black/70 backdrop-blur border border-white/10 px-2 py-1 text-[10px] font-semibold tracking-[0.2em] uppercase text-white">
              <span className="w-1.5 h-1.5 bg-orange-500 live-dot rounded-full"></span>
              Live
            </span>
          )}
          {isEndingSoon && !isEnded && (
            <span className="bg-red-600 text-white px-2 py-1 text-[10px] font-semibold tracking-[0.2em] uppercase">
              Ending Soon
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span className="bg-black/70 backdrop-blur border border-white/10 px-2 py-1 text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-200">
            {auction.unit_size}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-orange-400 mb-1">
            {categoryLabel(auction.category)}
          </div>
          <h3 className="font-display text-lg sm:text-xl font-bold uppercase leading-tight text-white line-clamp-2">
            {auction.title}
          </h3>
        </div>
      </div>
      <div className="p-4 grid grid-cols-2 gap-3 border-t border-zinc-800">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Current Bid</div>
          <div className="font-display text-2xl font-black text-orange-500 tabular-nums">
            {money(auction.current_bid || auction.starting_bid)}
          </div>
          <div className="flex items-center gap-1 text-xs text-zinc-500 mt-0.5">
            <Gavel className="w-3 h-3" /> {auction.bid_count || 0} bids
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Ends In</div>
          <Countdown endTime={auction.end_time} className="text-lg" testId={`countdown-${auction.id}`} />
          <div className="flex items-center justify-end gap-1 text-xs text-zinc-500 mt-0.5">
            <MapPin className="w-3 h-3" />
            {auction.city}, {auction.state}
          </div>
        </div>
      </div>
    </Link>
  );
}
